import { ListSourcedValue, Option, SourcedValue } from '../option.js';
import { optionIdSymbol, typeSymbol } from '../engine-impl.js';
import { pathToRoot, SourceType } from './PropertyPathWalker.js';
import { BigNumber, ListType, MLString, ObjectType, Property, SystemType, TypeKind, UnionType } from '../types.js';
import { SourceUtils } from './SourceUtils.js';
import { ModelSystemProperties } from './ModelSystemProperties.js';
import { ModelError, ModelErrorCode } from '../errors/ModelError.js';
import { Constraints } from '../constraint.js';
export class SimpleVisitor {
    constructor(schema) {
        this.schema = schema;
    }
    buildPrimitive(propValue) {
        switch (propValue.property.type.toLowerCase()) {
            case SystemType.MLString.toLowerCase():
                if (propValue.value instanceof MLString) {
                    return propValue.value;
                }
                return new MLString(propValue.value);
            case SystemType.BigNumber.toLowerCase():
                if (propValue.value instanceof BigNumber) {
                    return propValue.value;
                }
                return new BigNumber(propValue.value);
            default:
                return propValue.value;
        }
    }
    buildObject(properties, objectPropValue) {
        let object = {};
        for (let prop of properties) {
            let value = prop.value;
            if (value == null && prop.property.opts.default != null) {
                value = prop.property.opts.default;
            }
            object[prop.property.name] = value;
            const sourceUtils = new SourceUtils(this.schema);
            const originValue = objectPropValue.value[prop.property.name];
            if (sourceUtils.isSourceProvider(prop)) {
                if (sourceUtils.isSourceProviderHasSources(objectPropValue.value, prop.property.name)) {
                    ModelSystemProperties.addSourceToModel(object, prop.property.name, ModelSystemProperties.getSourceFromModel(objectPropValue.value, prop.property.name));
                }
                else {
                    const providerType = sourceUtils.sourceProviderType(prop);
                    if (providerType == SourceType.Single && !(originValue instanceof SourcedValue)) {
                        throw new Error(`SingleSV: Property:${pathToRoot(prop)} should have sourced values`);
                    }
                    if (providerType == SourceType.ListCommon && !(originValue instanceof SourcedValue)) {
                        throw new Error(`SingleSV: Property:${pathToRoot(prop)} should have sourced values`);
                    }
                    if (providerType == SourceType.ListCommonForNested && !(originValue instanceof ListSourcedValue)) {
                        throw new Error(`CommonSVWithNested: Property:${pathToRoot(prop)} should have sourced values`);
                    }
                    if (providerType == SourceType.ListSpecific && originValue.find(item => !(item instanceof SourcedValue))) {
                        throw new Error(`SpecificSV: Items inside property:${pathToRoot(prop)} should have sourced values`);
                    }
                    sourceUtils.setSourcesToModel(object, prop.property.name, originValue);
                }
            }
        }
        object[typeSymbol] = objectPropValue.property.type;
        if (objectPropValue.value._id) {
            SourceUtils.setOptionId(object, objectPropValue.value._id);
        }
        return object;
    }
}
export class ValidationVisitor extends SimpleVisitor {
    buildObject(properties, objectPropValue) {
        for (let prop of properties) {
            if (prop.property.opts.constraints.find(constant => constant.name === Constraints.Required) && prop.value === undefined) {
                throw new Error(`Required property (${prop.property.name}) is not defined`);
            }
        }
        return super.buildObject(properties, objectPropValue);
    }
}
export class ComponentSearchVisitor extends SimpleVisitor {
    constructor(schema, components) {
        super(schema);
        this.components = components;
        this.searchResult = [];
    }
    buildPrimitive(propValue) {
        return propValue.value;
    }
    buildObject(properties, objectPropValue) {
        const entity = objectPropValue.value;
        const typeRef = objectPropValue.property.type;
        const type = this.schema.getType(typeRef);
        if (type instanceof ObjectType && type.components.length > 0) {
            if (this.components.every(component => type.components.includes(component))) {
                this.searchResult.push({
                    entity,
                    path: pathToRoot(objectPropValue)
                });
            }
        }
        return entity;
    }
}
export class PrototypeTraverser {
    constructor(schema, typeRef, prototype) {
        this.schema = schema;
        this.typeRef = typeRef;
        this.prototype = prototype;
    }
    map(visitor) {
        const rootProperty = new Property('$', this.typeRef);
        const rootPropValue = { parent: undefined, property: rootProperty, value: this.prototype };
        const model = this._map(rootPropValue, visitor);
        return model;
    }
    _map(propValue, visitor) {
        var _a;
        if (propValue.value === undefined) {
            return;
        }
        const propertyMappedValue = SourceUtils.getInnerValue(propValue.value);
        let type = this.schema.getType(propValue.property.type);
        switch (type.kind) {
            case TypeKind.Primitive:
                return visitor.buildPrimitive(propValue);
            case TypeKind.List:
                if (!Array.isArray(propertyMappedValue)) {
                    throw new Error(`${propValue.property.name} should be a List`);
                }
                else {
                    if (!(type instanceof ListType))
                        throw new Error('Incorrect list type');
                    const itemType = type.elementType;
                    const items = propertyMappedValue.map((v, i) => {
                        const itemPropValue = { value: v, property: new Property(i.toString(), itemType), parent: propValue };
                        return this._map(itemPropValue, visitor);
                    });
                    items[typeSymbol] = propValue.property.type;
                    return items;
                }
            case TypeKind.Enum:
                return visitor.buildPrimitive(propValue);
            case TypeKind.Union:
                if (!(type instanceof UnionType)) {
                    throw new ModelError(ModelErrorCode.UNION_TYPE_VALUE_EXPECTED, `Type '${propValue.property.type}' should be an union type`);
                }
                const unwrappedValue = (_a = propValue.parent) === null || _a === void 0 ? void 0 : _a.value[propValue.property.name];
                let runtimeTypeRef = null;
                if (!runtimeTypeRef && unwrappedValue instanceof Option) {
                    runtimeTypeRef = unwrappedValue.type.typeRef;
                }
                if (!runtimeTypeRef) {
                    runtimeTypeRef = ModelSystemProperties.getExplicitType(propValue.value);
                }
                if (!runtimeTypeRef) {
                    runtimeTypeRef = ModelSystemProperties.getTypeRef(unwrappedValue);
                }
                if (!runtimeTypeRef && ModelSystemProperties.getTypeRef(propValue.parent.value)) {
                    const parent = propValue.parent.value;
                    runtimeTypeRef = ModelSystemProperties.getTypeRef(parent[propValue.property.name]);
                }
                if (!runtimeTypeRef) {
                    const path = pathToRoot(propValue);
                    throw new ModelError(ModelErrorCode.UNION_TYPE_VALUE_MISSING_RUNTIME_TYPE, `Cannot determine runtime type of ${type.name} for "${path}". Try to use Options or specify explicit type with _type:<Type>`);
                }
                let runtimeType = this.schema.getType(runtimeTypeRef);
                if (!runtimeType) {
                    throw new Error(`Type ${runtimeTypeRef} not found`);
                }
                if (!type.types.find(t => t === runtimeTypeRef)) {
                    throw new Error(`Type '${runtimeType.name}' is not in a list of ${type.name} union`);
                }
                type = runtimeType;
                propValue.property = Object.assign(Object.assign({}, propValue.property), { type: type.name });
            case TypeKind.Object:
                if (!(type instanceof ObjectType)) {
                    throw new Error(`Type '${propValue.property.type}' should be an object type`);
                }
                if (propertyMappedValue) {
                    const properties = [];
                    for (const property of type.properties) {
                        properties.push(property.name);
                    }
                    properties.sort().reverse();
                    const objectProperties = [];
                    while (properties.length > 0) {
                        let currentPropertyName = properties.pop();
                        if (!currentPropertyName) {
                            continue;
                        }
                        let property = type.getProperty(currentPropertyName);
                        if (!property) {
                            throw new Error(`Property '${currentPropertyName}' not exist in type ${type.getTypeReference()}`);
                        }
                        const value = SourceUtils.getInnerValue(propertyMappedValue[currentPropertyName]);
                        const itemPropValue = { property: property, value: value, parent: propValue };
                        objectProperties.push({ property: property, value: this._map(itemPropValue, visitor), parent: propValue });
                    }
                    let obj = visitor.buildObject(objectProperties, propValue);
                    if (propertyMappedValue && propertyMappedValue[optionIdSymbol] && obj) {
                        obj[optionIdSymbol] = propertyMappedValue[optionIdSymbol];
                    }
                    return obj;
                }
        }
    }
}
//# sourceMappingURL=PrototypeVisitor.js.map